<template>
    <v-card class="mx-auto" max-width="1400" elevation="2">
        <v-card-title class="primary white--text py-3">
            <span class="headline">Panel de Prospectos</span>
            <v-spacer></v-spacer>

            <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar prospecto..." single-line
                hide-details dense outlined background-color="white" class="search-field mr-4"></v-text-field>

            <v-btn color="amber darken-2" dark @click="exportToExcel" :loading="exporting"
                :disabled="filteredProspects.length === 0">
                <v-icon left>mdi-microsoft-excel</v-icon>
                Exportar XLSX
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
            <!-- Filtros rápidos -->
            <v-row dense>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-menu v-model="menuFechaInicio" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="filters.fechaInicio" label="Fecha inicial"
                                prepend-icon="mdi-calendar-start" readonly dense outlined hide-details clearable
                                v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.fechaInicio" :max="maxFecha" no-title scrollable
                            @input="menuFechaInicio = false"></v-date-picker>
                    </v-menu>
                </v-col>

                <!-- Fecha Final -->
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-menu v-model="menuFechaFin" :close-on-content-click="false" transition="scale-transition"
                        offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="filters.fechaFin" label="Fecha final" prepend-icon="mdi-calendar-end"
                                readonly dense outlined hide-details clearable v-bind="attrs" v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.fechaFin" :min="filters.fechaInicio" :max="maxFecha" no-title
                            scrollable @input="menuFechaFin = false"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.etiqueta" :items="etiquetas" item-text="nombre" item-value="id"
                        label="Etiqueta" clearable dense outlined hide-details :menu-props="{ offsetY: true }">
                        <template v-slot:selection="{ item }">
                            <v-chip small :color="item.color" text-color="white" class="mr-2">
                                {{ item.nombre }}
                            </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-chip small :color="item.color" text-color="white">
                                {{ item.nombre }}
                            </v-chip>
                        </template>
                    </v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.ventana" :items="ventanaOptions" label="Ventana de tiempo" clearable
                        dense outlined hide-details></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-select v-model="filters.plantel" :items="plantelesUnicos" label="Plantel" clearable dense
                        outlined hide-details></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-btn color="grey lighten-2" small @click="resetFilters" class="mt-6">
                        Limpiar filtros
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="3" lg="2">
                    <v-btn color="grey lighten-2" small @click="getData" class="mt-6">
                        Actualizar
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Tabla principal -->
            <v-data-table :headers="headers" :items="filteredProspects" :search="search" :items-per-page="15"
                :loading="loading" class="elevation-1 mt-4" dense fixed-header height="calc(100vh - 320px)">
                <template v-slot:item.idetiqueta="{ item }">
                    <v-chip small :color="getEtiquetaColor(item.idetiqueta)" text-color="white">
                        {{ getEtiquetaNombre(item.idetiqueta) }}
                    </v-chip>
                </template>

                <template v-slot:item.tiemporestante="{ item }">
                    <v-chip :color="item.tiemporestante <= 24 ? 'success' : 'grey darken-1'" small text-color="white">
                        {{ item.tiemporestante }} h
                    </v-chip>
                </template>

                <template v-slot:item.fecha_creacion="{ item }">
                    {{ formatDate(item.fecha_creacion) }}
                </template>

                <template v-slot:item.fecha_ultimo_mensaje="{ item }">
                    {{ formatDate(item.fecha_ultimo_mensaje) }}
                </template>

                <template v-slot:no-data>
                    <v-alert :value="true" color="warning" icon="mdi-alert">
                        No se encontraron prospectos con los filtros actuales
                    </v-alert>
                </template>
            </v-data-table>
        </v-card-text>

        <v-card-actions class="justify-center grey lighten-4 py-2">
            <span class="caption grey--text">
                Total prospectos: <strong>{{ filteredProspects.length }}</strong>
            </span>
        </v-card-actions>
    </v-card>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
    name: 'ProspectosPanel',

    data: () => ({
        search: '',
        loading: false,
        exporting: false,

        prospects: [], // Aquí irá tu array de objetos

        menuFechaInicio: false,
        menuFechaFin: false,

        prospects: [],

        filters: {
            fechaInicio: null,    // formato 'YYYY-MM-DD'
            fechaFin: null,
            etiqueta: null,
            ventana: null,
            plantel: null
        },

        ventanaOptions: [
            { text: 'Ventana ABIERTA (≤ 24h)', value: 'abierta' },
            { text: 'Ventana CERRADA (> 24h)', value: 'cerrada' }
        ],

        headers: [
            { text: 'Folio', value: 'folio', width: '110px' },
            { text: 'Nombre', value: 'nombre_completo' },
            { text: 'Teléfono', value: 'telefono', width: '130px' },
            { text: 'Etiqueta', value: 'idetiqueta', width: '180px', sortable: false },
            { text: 'Vendedora', value: 'vendedora' },
            { text: 'Plantel', value: 'plantel' },
            { text: 'Ventana', value: 'tiemporestante', width: '110px', align: 'center' },
            { text: 'Últ. Mensaje', value: 'fecha_ultimo_mensaje', width: '140px' },
            { text: 'Creado', value: 'fecha_creacion', width: '140px' }
        ],

        etiquetas: [
            { id: 1, nombre: 'PENDIENTE DE PAGO', color: '#F44336' },
            { id: 2, nombre: 'CLIENTE POTENCIAL', color: '#29B6F6' },
            { id: 3, nombre: 'EXAMEN REALIZADO', color: '#FF9800' },
            { id: 4, nombre: 'PAGO PARCIAL', color: '#D4AF37' },
            { id: 5, nombre: 'INSCRITO', color: '#4CAF50' },
            { id: 6, nombre: 'VENTANA DE TIEMPO', color: '#4A148C' },
            { id: 7, nombre: 'SPAM', color: '#000000' },
            { id: 8, nombre: 'CON CITA AGENDADA', color: '#9C27B0' },
            { id: 9, nombre: 'CLIENTE NO CONTESTA', color: '#B0BEC5' },
            { id: 10, nombre: 'NO INTERESADO', color: '#546E7A' },
            { id: 11, nombre: 'EXAMEN ENVIADO', color: '#FFEB3B' },
            { id: 12, nombre: 'NUEVO', color: '#E040FB' },
            { id: 13, nombre: 'PRIMER CONTACTO', color: '#00BCD4' },
            { id: 14, nombre: 'LLAMADA AGENDADA', color: '#3F51B5' },
            { id: 15, nombre: 'NUEVO WEB', color: '#E040FB' }
        ]
    }),

    computed: {
        maxFecha() {
            return new Date().toISOString().split('T')[0] // hoy
        },
        filteredProspects() {
            let result = [...this.prospects]

            // Filtro por etiqueta
            if (this.filters.etiqueta) {
                result = result.filter(p => p.idetiqueta === this.filters.etiqueta)
            }

            // Filtro por ventana de tiempo
            if (this.filters.ventana) {
                if (this.filters.ventana === 'abierta') {
                    result = result.filter(p => p.tiemporestante <= 24)
                } else {
                    result = result.filter(p => p.tiemporestante > 24)
                }
            }

            // Filtro por plantel
            if (this.filters.plantel) {
                result = result.filter(p => p.plantel === this.filters.plantel)
            }

            return result
        },

        plantelesUnicos() {
            const planteles = new Set(this.prospects.map(p => p.plantel))
            return [...planteles].sort()
        }
    },
    created(){
        // this.getData()
    },
    methods: {
        getEtiquetaColor(id) {
            const etiqueta = this.etiquetas.find(e => e.id === id)
            return etiqueta ? etiqueta.color : 'grey'
        },

        getEtiquetaNombre(id) {
            const etiqueta = this.etiquetas.find(e => e.id === id)
            return etiqueta ? etiqueta.nombre : 'Sin etiqueta'
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        resetFilters() {
            this.filters = {
                etiqueta: null,
                ventana: null,
                plantel: null
            }
            this.search = ''
        },

        exportToExcel() {
            if (this.filteredProspects.length === 0) return

            this.exporting = true

            try {
                // Preparar datos para exportar
                const data = this.filteredProspects.map(p => ({
                    Folio: p.folio,
                    Nombre: p.nombre_completo,
                    Teléfono: p.telefono,
                    Etiqueta: this.getEtiquetaNombre(p.idetiqueta),
                    Vendedora: p.vendedora,
                    Plantel: p.plantel,
                    'Ventana (horas)': p.tiemporestante,
                    'Último mensaje': this.formatDate(p.fecha_ultimo_mensaje),
                    Creado: this.formatDate(p.fecha_creacion)
                }))

                const worksheet = XLSX.utils.json_to_sheet(data)
                const workbook = XLSX.utils.book_new()

                XLSX.utils.book_append_sheet(workbook, worksheet, "Prospectos")

                // Autoajuste de columnas (opcional pero mejora mucho la experiencia)
                const colWidths = []
                const range = XLSX.utils.decode_range(worksheet['!ref'])
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxWidth = 10
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = worksheet[XLSX.utils.encode_cell({ c: C, r: R })]
                        if (cell && cell.v) {
                            maxWidth = Math.max(maxWidth, String(cell.v).length + 2)
                        }
                    }
                    colWidths.push({ wch: Math.min(maxWidth, 60) })
                }
                worksheet['!cols'] = colWidths

                XLSX.writeFile(workbook, `Prospectos_${new Date().toISOString().slice(0, 10)}.xlsx`)
            } catch (error) {
                console.error('Error al exportar:', error)
                this.$toast.error('Error al generar el archivo Excel')
            } finally {
                this.exporting = false
            }
        },
        async getData() {
            this.$http.post('obtener.prospectos.fecha', { escuela: 1, fechaini: this.filters.fechaInicio, fechafin: this.filters.fechaFin }).then((response) => {
                this.prospects = response.data
            }).catch(error => {
                //   this.validarError( error.response.data.message )
            })
        }
    },

    // Ejemplo de cómo podrías cargar los datos
}
</script>

<style scoped>
.search-field {
    max-width: 320px;
}

.v-data-table>>>.v-data-table-header th {
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
}
</style>